"use client";

import { FC, useEffect, useState, useRef } from "react";
import {
  getEntryDocumentsAction,
  updateEntryAdditionalDetailsAction,
} from "@/app/_lib/actions/ledger.actions";
import { getAllVendorsByOrganizationIdLazyAction } from "@/app/_lib/actions/vendor.actions";
import { updateDocumentsAction } from "@/app/_lib/actions/document.actions";
import ImageUploader from "@/app/_lib/components/cloudinary/image-uploader";
import DocumentPicker from "./document-picker";
import { DocumentTypes } from "@/prisma-package/src";
import {
  DrawerStates,
  EntryDetailsDrawerData,
  drawerAtom,
} from "@/app/_lib/atoms/drawer.atom";
import { useSession } from "next-auth/react";
import { useAtom } from "jotai";
import { MdSearch } from "react-icons/md";
import CloudImage from "@/app/_lib/components/cloudinary/cloud-image";
import useDocumentLocator, {
  DocumentLocatorStatus,
} from "@/app/_lib/hooks/use-document-locator";
import { documentTypeLabel } from "@/app/_lib/utils/document-category.utils";
import Dinero from "dinero.js";
import { toCents } from "@/prisma-package/src";
import { FetchStates } from "@/app/_lib/types/utility.types";

type AttachedDoc = {
  documentId: string | null;
  url: string;
  type: string;
  // Type when loaded/attached, so we know when to persist a correction
  originalType: string;
};

const ENTRY_DOCUMENT_TYPES: string[] = [
  DocumentTypes.INVOICE,
  DocumentTypes.RECEIPT,
  DocumentTypes.PROOF_OF_PAYMENT,
];

const UpdateAddtionalEntryDetails: FC = () => {
  const { data: session } = useSession();
  const userId = session?.user?.id;
  const [drawer, setDrawer] = useAtom(drawerAtom);
  const [formErrors, setFormErrors] = useState<string>();
  const [uploadStatus, setUploadStatus] = useState<FetchStates>("idle");
  const [vendors, setVendors] = useState<any[]>([]);
  const [isLoadingVendors, setIsLoadingVendors] = useState(false);
  const [vendorSearchTerm, setVendorSearchTerm] = useState<string>("");
  const [showVendorDropdown, setShowVendorDropdown] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<{ id: string; name: string } | null>(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>("");
  const vendorSearchRef = useRef<HTMLDivElement>(null);
  const vendorDebounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [attachedDocs, setAttachedDocs] = useState<AttachedDoc[]>([]);

  const addDocuments = (docs: AttachedDoc[]) => {
    setAttachedDocs((prev) => {
      const next = [...prev];
      for (const doc of docs) {
        const exists = next.some((d) =>
          doc.documentId
            ? d.documentId === doc.documentId
            : d.url === doc.url && d.type === doc.type
        );
        if (!exists) next.push(doc);
      }
      return next;
    });
  };

  const { docLocateStatus, locateDocuments } = useDocumentLocator({
    projectId: (drawer.data as EntryDetailsDrawerData).projectId,
    afterLocate: (results) => {
      if (results && results.length > 0) {
        addDocuments(
          results.map((result) => ({
            documentId: result.id,
            url: result.secureUrl,
            type: result.type,
            originalType: result.type,
          }))
        );
      }
    },
  });

  const isLocatingDocument = docLocateStatus === "loading";

  useEffect(() => {
    if (uploadStatus === "success") {
      setUploadStatus("idle");
      setDrawer({ state: DrawerStates.DISMISSED });
    }
  }, [uploadStatus, setDrawer]);

  // Search vendors with debouncing
  useEffect(() => {
    if (vendorSearchTerm.length >= 2 && drawer.data) {
      const drawerData = drawer.data as EntryDetailsDrawerData;
      if (drawerData.orgId) {
        if (vendorDebounceTimer.current) {
          clearTimeout(vendorDebounceTimer.current);
        }

        vendorDebounceTimer.current = setTimeout(async () => {
          setIsLoadingVendors(true);
          try {
            const [error, loadedVendors] = await getAllVendorsByOrganizationIdLazyAction({
              organizationId: drawerData.orgId!,
              opts: { vendorName: vendorSearchTerm }
            });
            if (!error && loadedVendors) {
              setVendors(loadedVendors.vendors || []);
            }
          } catch (err) {
            console.error("Failed to search vendors:", err);
          } finally {
            setIsLoadingVendors(false);
          }
        }, 300);
      }
    } else {
      setVendors([]);
    }

    return () => {
      if (vendorDebounceTimer.current) {
        clearTimeout(vendorDebounceTimer.current);
      }
    };
  }, [vendorSearchTerm, drawer.data]);

  // Handle clicks outside vendor dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (vendorSearchRef.current && !vendorSearchRef.current.contains(event.target as Node)) {
        setShowVendorDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Initialize state with existing values and load existing vendor
  useEffect(() => {
    if (drawer.data) {
      const drawerData = drawer.data as EntryDetailsDrawerData;
      setSelectedEmployeeId(drawerData.fullEntryData.employeeId || "");

      // If there's an existing vendor, load it
      if (drawerData.fullEntryData.vendorId && drawerData.fullEntryData.vendorName) {
        setSelectedVendor({
          id: drawerData.fullEntryData.vendorId,
          name: drawerData.fullEntryData.vendorName
        });
      }
    }
  }, [drawer.data]);

  // Load the entry's document associations, falling back to legacy URL
  // fields for entries that have URLs but no association records
  useEffect(() => {
    if (!drawer.data) return;
    const drawerData = drawer.data as EntryDetailsDrawerData;
    const { fullEntryData, projectId, ledgerUploadId } = drawerData;

    (async () => {
      const [error, entryDocuments] = await getEntryDocumentsAction({
        projectId,
        ledgerUploadId,
        entryId: fullEntryData.id,
      });

      const docs: AttachedDoc[] = [];

      if (!error && entryDocuments) {
        for (const entryDocument of entryDocuments) {
          // Use the document's live type, not the association's snapshot —
          // the document may have been retyped since it was attached
          docs.push({
            documentId: entryDocument.documentId,
            url: entryDocument.document.secureUrl,
            type: entryDocument.document.type,
            originalType: entryDocument.document.type,
          });
        }
      }

      const legacyUrls: [string | null, DocumentTypes][] = [
        [fullEntryData.invoiceUrl, DocumentTypes.INVOICE],
        [fullEntryData.receiptUrl, DocumentTypes.RECEIPT],
        [fullEntryData.proofOfPaymentUrl, DocumentTypes.PROOF_OF_PAYMENT],
      ];

      for (const [url, type] of legacyUrls) {
        if (url && !docs.some((doc) => doc.url === url)) {
          docs.push({ documentId: null, url, type, originalType: type });
        }
      }

      setAttachedDocs(docs);
    })();
  }, [drawer.data]);

  if (!userId || !drawer.data) return null;

  const {
    fullEntryData: {
      id,
      vendorName,
      docId,
      amount,
      paymentNumber,
    },
    payrollEmployees,
    projectId,
    ledgerUploadId,
    orgId,
  } = drawer.data as EntryDetailsDrawerData;

  const amountAsCurrency = Dinero({ amount: toCents(amount) }).toFormat(
    "0,0.00"
  );

  const handleAutoLocate = () => {
    const invoiceTerms = [];
    if (docId) invoiceTerms.push(docId);
    if (vendorName) invoiceTerms.push(vendorName);
    if (amount) invoiceTerms.push(amountAsCurrency);

    const proofOfPaymentTerms = [];
    if (paymentNumber && !paymentNumber.includes("VOID"))
      proofOfPaymentTerms.push(paymentNumber);
    if (vendorName) proofOfPaymentTerms.push(vendorName);
    if (amount) proofOfPaymentTerms.push(amountAsCurrency);

    locateDocuments([
      {
        queryTerms: invoiceTerms,
        documentType: DocumentTypes.INVOICE,
        excludeOrgDocuments: true,
      },
      {
        queryTerms: [vendorName, amountAsCurrency],
        documentType: DocumentTypes.RECEIPT,
        excludeOrgDocuments: true,
      },
      {
        queryTerms: proofOfPaymentTerms,
        documentType: DocumentTypes.PROOF_OF_PAYMENT,
        excludeOrgDocuments: true,
      },
    ]);
  };

  const handleUpdateDetails = async () => {
    setUploadStatus("loading");

    const firstUrlOfType = (type: DocumentTypes) =>
      attachedDocs.find((doc) => doc.type === type)?.url ?? null;

    // Persist any type corrections to the documents themselves
    for (const doc of attachedDocs) {
      if (doc.documentId && doc.type !== doc.originalType) {
        const [typeErr] = await updateDocumentsAction({
          documentId: doc.documentId,
          type: doc.type,
          projectId,
        });

        if (typeErr) {
          setUploadStatus("error");
          setFormErrors(typeErr.message);
          return;
        }
      }
    }

    const [err] = await updateEntryAdditionalDetailsAction({
      projectId,
      ledgerUploadId,
      entryId: id,
      employeeId: selectedEmployeeId || undefined,
      vendorId: selectedVendor?.id || undefined,
      invoiceUrl: firstUrlOfType(DocumentTypes.INVOICE),
      receiptUrl: firstUrlOfType(DocumentTypes.RECEIPT),
      proofOfPaymentUrl: firstUrlOfType(DocumentTypes.PROOF_OF_PAYMENT),
      documents: attachedDocs
        .filter((doc) => doc.documentId)
        .map((doc) => ({
          documentId: doc.documentId!,
          // Entry-document attachments are always built-in expense types.
          documentType: doc.type as DocumentTypes,
        })),
    });

    if (err) {
      setUploadStatus("error");
      setFormErrors(err.message);
      return;
    }

    setUploadStatus("success");
  };

  return (
    <div>
      <div className="form-control w-full max-w-md mb-4" ref={vendorSearchRef}>
        <div className="label">
          <span className="label-text">Select A Vendor:</span>
        </div>
        <div className="relative">
          <input
            type="text"
            className="input input-bordered w-full"
            placeholder="Search for a vendor..."
            value={selectedVendor ? selectedVendor.name : vendorSearchTerm}
            onChange={(e) => {
              setVendorSearchTerm(e.target.value);
              setSelectedVendor(null);
              setShowVendorDropdown(true);
            }}
            onFocus={() => setShowVendorDropdown(true)}
          />
          {selectedVendor && (
            <button
              type="button"
              className="btn btn-ghost btn-xs absolute right-2 top-2"
              onClick={() => {
                setSelectedVendor(null);
                setVendorSearchTerm("");
                setVendors([]);
              }}
            >
              Clear
            </button>
          )}
          {showVendorDropdown && vendorSearchTerm.length >= 2 && (
            <div className="absolute z-10 w-full mt-1 bg-base-100 border border-base-300 rounded-md shadow-lg max-h-60 overflow-auto">
              {isLoadingVendors ? (
                <div className="p-4 text-center">
                  <span className="loading loading-spinner loading-sm" />
                  <span className="ml-2">Searching vendors...</span>
                </div>
              ) : vendors.length > 0 ? (
                vendors.map((vendor) => (
                  <button
                    key={vendor.id}
                    type="button"
                    className="w-full p-3 text-left hover:bg-base-200 transition-colors"
                    onClick={() => {
                      setSelectedVendor({ id: vendor.id, name: vendor.name });
                      setShowVendorDropdown(false);
                      setVendorSearchTerm("");
                    }}
                  >
                    <div className="font-semibold">{vendor.name}</div>
                    {vendor.ein && <div className="text-sm text-base-content/60">EIN: {vendor.ein}</div>}
                  </button>
                ))
              ) : (
                <div className="p-4 text-center text-base-content/60">
                  No vendors found
                </div>
              )}
            </div>
          )}
          {showVendorDropdown && vendorSearchTerm.length < 2 && vendorSearchTerm.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-base-100 border border-base-300 rounded-md shadow-lg p-4 text-center text-base-content/60">
              Type at least 2 characters to search
            </div>
          )}
        </div>
      </div>
      <label className="form-control w-full max-w-md">
        <div className="label">
          <span className="label-text">Select An Employee:</span>
        </div>
        <select
          title="Select Employee"
          className="select block w-full"
          value={selectedEmployeeId}
          onChange={(e) => setSelectedEmployeeId(e.target.value)}
        >
          <option value="">Select an Employee</option>
          {payrollEmployees.map((employee, idx) => (
            <option key={`${employee.id}_${idx}`} value={employee.id}>
              {employee.lastName}
              {employee.jobTitle ? ` - ${employee.jobTitle}` : ""}
            </option>
          ))}
        </select>
      </label>
      <div className="flex flex-col gap-2 mb-4 mt-8">
        <span>Documents:</span>
        {attachedDocs.length > 0 && (
          <div className="flex flex-wrap gap-4 py-2">
            {attachedDocs.map((doc) => {
              const typeOptions = ENTRY_DOCUMENT_TYPES.includes(doc.type)
                ? ENTRY_DOCUMENT_TYPES
                : [doc.type, ...ENTRY_DOCUMENT_TYPES];

              return (
                <div
                  key={`${doc.documentId ?? doc.url}_${doc.originalType}`}
                  className="flex flex-col gap-2"
                >
                  <select
                    title="Document Type"
                    className="select select-bordered select-sm"
                    value={doc.type}
                    onChange={(e) => {
                      const type = e.target.value as DocumentTypes;
                      setAttachedDocs((prev) =>
                        prev.map((d) => (d === doc ? { ...d, type } : d))
                      );
                    }}
                  >
                    {typeOptions.map((type) => (
                      <option key={type} value={type}>
                        {documentTypeLabel(type)}
                      </option>
                    ))}
                  </select>
                  <CloudImage
                    src={doc.url}
                    width={250}
                    height={250}
                    alt={documentTypeLabel(doc.type)}
                    documentType={doc.type}
                  />
                  <button
                    type="button"
                    className="btn btn-ghost btn-xs"
                    onClick={() => {
                      setAttachedDocs((prev) => prev.filter((d) => d !== doc));
                    }}
                  >
                    Remove
                  </button>
                </div>
              );
            })}
          </div>
        )}
        <div className="flex items-center gap-4 py-2">
          <ImageUploader
            projectId={projectId}
            organizationId={orgId}
            callToAction="Upload Document"
            createdBy={userId}
            showLinkExisting={false}
            onSuccess={(info) => {
              if (!info) return;
              const documents = Array.isArray(info) ? info : [info];
              addDocuments(
                documents.map((document) => ({
                  documentId: document.id,
                  url: document.secureUrl,
                  type: document.type as DocumentTypes,
                  originalType: document.type as DocumentTypes,
                }))
              );
            }}
          />
          <DocumentPicker
            projectId={projectId}
            attachedDocumentIds={attachedDocs
              .filter((doc) => doc.documentId)
              .map((doc) => doc.documentId!)}
            onSelect={(doc) => {
              addDocuments([
                {
                  documentId: doc.id,
                  url: doc.secureUrl,
                  type: doc.type,
                  originalType: doc.type,
                },
              ]);
            }}
          />
          <button
            className="btn btn-md text-cnd-purple border border-cnd-purple bg-white"
            type="button"
            onClick={handleAutoLocate}
            disabled={isLocatingDocument}
          >
            {isLocatingDocument ? (
              "Locating..."
            ) : (
              <>
                <MdSearch /> Auto-locate Documents
              </>
            )}
          </button>
        </div>
      </div>
      {docLocateStatus === DocumentLocatorStatus.NoResults && (
        <p className="text-error my-3">
          No matching documents found in the system.
        </p>
      )}
      {formErrors && <p className="text-error my-4">{formErrors}</p>}
      <button
        className="btn btn-primary mt-4"
        disabled={uploadStatus === "loading"}
        type="button"
        onClick={handleUpdateDetails}
      >
        Update Details
      </button>
    </div>
  );
};

export default UpdateAddtionalEntryDetails;
